import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, from, of } from 'rxjs';
import { catchError, map, reduce, shareReplay, switchMap } from 'rxjs/operators';

import {
    BaseNestedFieldDefinition,
    ComplexMapFieldValue,
    CustomHttpParamEncoder,
    DottedKey,
    FieldDefinitionHelper,
    FieldValue,
    PaginatedResults,
    PaginationInfo,
    ShFieldDefinitionService,
    combineLatestAlwaysEmit,
    isString
} from '@stylehaven/components';
import { isArray } from 'lodash-es';
import { GenericQueryPart, Operator, Strategy } from '../entities/generic-query-part.entity';
import { PartialWebserviceField, WebserviceConfig, WebserviceField } from '../entities/webservice-config.entity';
import { WebserviceData } from '../entities/webservice-data.entity';
import { RSQLQueryBuilder } from '../helpers/rsql-query-builder';

@Injectable()
export class ExternalWebservice {
    protected webserviceConfig: WebserviceConfig;

    private defaultPagination: PaginationInfo = {
        page: 0,
        resultsPerPage: 100
    };

    protected staticWebserviceData$: Observable<WebserviceData[]>;

    // Injectors
    private fieldDefinitionService: ShFieldDefinitionService = inject(ShFieldDefinitionService);

    constructor(protected http: HttpClient) {}

    // #region Initialization
    public initWebservice(config: WebserviceConfig): void {
        this.webserviceConfig = config;

        if (!this.webserviceConfig.isSearchable) {
            this.staticWebserviceData$ = this.getWebserviceData(this.webserviceConfig.url).pipe(shareReplay(1));
        }
    }
    // #endregion

    // #region Search
    protected getWebserviceData(request: string): Observable<WebserviceData[]> {
        return this.http.get<WebserviceData[]>(request).pipe(catchError(error => this.handleWebserviceError(error)));
    }

    public globalSearch(term: string): Observable<ComplexMapFieldValue[]> {
        const queryPart: GenericQueryPart = {
            strategy: Strategy.AND,
            operator: Operator.CONTAINS,
            field: this.webserviceConfig.globalSearchField,
            value: term
        };

        return this.search([queryPart]).pipe(map(paginated => paginated.results));
    }

    public search(
        queryParts: GenericQueryPart[],
        pagination?: PaginationInfo
    ): Observable<PaginatedResults<ComplexMapFieldValue>> {
        if (this.webserviceConfig.isSearchable) {
            return this.webserviceSearch(queryParts, pagination);
        }

        return this.internalSearch(queryParts, pagination);
    }

    private webserviceSearch(
        queryParts: GenericQueryPart[],
        pagination: PaginationInfo = this.defaultPagination
    ): Observable<PaginatedResults<ComplexMapFieldValue>> {
        // For now RSQL used since this is the query language used for KazerneDossin
        // In future might be useful to specify query language in config and switch based on this
        const webserviceQuery: string = RSQLQueryBuilder.build(queryParts);

        const httpParams: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set(
            'q',
            webserviceQuery
        );
        const request = `${this.webserviceConfig.url}?${httpParams.toString()}`;

        return this.getWebserviceData(request).pipe(
            shareReplay(1),
            switchMap(jsonData => this.parseWebserviceData(jsonData, pagination)),
            shareReplay(1)
        );
    }

    private parseWebserviceData(
        data: WebserviceData[],
        pagination: PaginationInfo
    ): Observable<PaginatedResults<ComplexMapFieldValue>> {
        const startIndex: number = pagination.page * pagination.resultsPerPage;
        const endIndex: number = startIndex + pagination.resultsPerPage;

        const convertedData$: Observable<ComplexMapFieldValue[]> = this.webserviceDataListToMetadataList(
            data.slice(startIndex, endIndex)
        );
        return convertedData$.pipe(
            map(convertedData => ({
                paginationInfo: {
                    ...pagination,
                    totalNumberOfResults: data.length
                },
                results: convertedData
            }))
        );
    }

    private internalSearch(
        queryParts: GenericQueryPart[],
        pagination?: PaginationInfo
    ): Observable<PaginatedResults<ComplexMapFieldValue>> {
        throw new Error('Method not implemented.');
    }
    // #endregion

    // #region Metadata parser helpers
    private webserviceDataListToMetadataList(data: WebserviceData[]): Observable<ComplexMapFieldValue[]> {
        const fieldValues$: Observable<ComplexMapFieldValue[]> = combineLatestAlwaysEmit(
            data.map(object => this.webserviceDataToMetadata(object, this.webserviceConfig.fields))
        );

        return fieldValues$;
    }

    private webserviceDataToMetadata(
        dataObject: WebserviceData,
        wsFieldConfig: PartialWebserviceField[]
    ): Observable<ComplexMapFieldValue> {
        return from(Object.entries(dataObject)).pipe(
            switchMap(([key, webserviceValue]) =>
                this.webserviceDataItemToMetadata(key, webserviceValue, wsFieldConfig)
            ),
            reduce((metadata, [metadataValue, fieldKey]) => {
                if (!!metadataValue) {
                    metadata[fieldKey] = metadataValue;
                }
                return metadata;
            }, {} as ComplexMapFieldValue)
        );
    }

    private webserviceDataItemToMetadata(
        key: string,
        webserviceValue: any,
        wsFieldConfig: PartialWebserviceField[]
    ): Observable<[any, string]> {
        let webserviceField: PartialWebserviceField;

        if (wsFieldConfig.length === 1 && !Object.keys(wsFieldConfig[0]).includes('Key')) {
            webserviceField = wsFieldConfig[0];
        } else {
            const config: WebserviceField | undefined = wsFieldConfig
                .filter((field): field is WebserviceField => field.hasOwnProperty('Key'))
                .find(field => field.Key === key);

            if (!config) {
                // Mapping not defined. Skip field.
                return of([undefined, '']);
            }

            webserviceField = config;
        }

        const fieldDefDottedKey: DottedKey | undefined = webserviceField.FieldDefinitionDottedKey;

        return this.fieldDefinitionService.getByDottedKey(fieldDefDottedKey).pipe(
            switchMap(fieldDefinition => {
                if (!fieldDefinition) {
                    console.error(
                        `ExternalWebservice:: Failed to map webservice field '${key}'  to fielddefinition '${fieldDefDottedKey}'`
                    );
                    return of([undefined, ''] as [any, string]);
                }

                let metadata$: Observable<any>;
                if (FieldDefinitionHelper.IsList(fieldDefinition)) {
                    metadata$ = this.webserviceListFieldToMetadataListField(webserviceValue, webserviceField);
                } else {
                    metadata$ = this.webserviceFieldToMetadataField(webserviceValue, webserviceField);
                }

                return metadata$.pipe(map(metadata => [metadata, fieldDefinition.Key] as [any, string]));
            })
        );
    }

    private webserviceFieldToMetadataField(
        dataObject: WebserviceData,
        webserviceField: PartialWebserviceField
    ): Observable<ComplexMapFieldValue> {
        if (webserviceField.Children && webserviceField.Children.length > 0) {
            return this.webserviceDataToMetadata(dataObject, webserviceField.Children);
        }

        return of(dataObject);
    }

    private webserviceListFieldToMetadataListField(
        webserviceValue: WebserviceData,
        webserviceField: PartialWebserviceField
    ): Observable<Record<string, FieldValue[]> | []> {
        const wsValues: any[] =
            !isString(webserviceValue) && isArray(webserviceValue) ? webserviceValue : [webserviceValue];

        const listItemConfig: PartialWebserviceField | undefined = webserviceField.Children
            ? webserviceField.Children[0]
            : undefined;

        const dottedKey: DottedKey | undefined = listItemConfig?.FieldDefinitionDottedKey;

        const childFieldDefinition$: Observable<BaseNestedFieldDefinition | undefined> = dottedKey
            ? this.fieldDefinitionService.getByDottedKey(dottedKey)
            : of(undefined);

        return childFieldDefinition$.pipe(
            switchMap(childFieldDefinition => {
                if (!listItemConfig || !childFieldDefinition) {
                    console.error(
                        `ExternalWebservice:: Missing mapping configuration for list-items of ${webserviceField.FieldDefinitionDottedKey}`
                    );
                    return [];
                }

                return combineLatestAlwaysEmit(
                    wsValues.map(wsValue => this.webserviceFieldToMetadataField(wsValue, listItemConfig))
                ).pipe(
                    map(metadataArrayValue => {
                        const list: Record<string, FieldValue[]> = {};
                        list[childFieldDefinition.Key] = metadataArrayValue;

                        return list;
                    })
                );
            })
        );
    }

    // #endregion

    // Error handlers
    protected handleWebserviceError(error: Error): Observable<WebserviceData[]> {
        console.error(`ExternalWebservice:: Failed to retreive webservice data`, error);
        return of(JSON.parse('[]'));
    }
}
